import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ENVIRONMENT, Environment, ValidationError } from '@netfoundry-ui/shared/model';
import { firstValueFrom, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { LoggerService } from './logger.service';
import { checkFrontdoorBody } from './frontdoor-v3-validation';
import { HTTP_CLIENT } from './token.service';

/** A frontdoor as the v3 API lists it. Only the fields the picker and the agent pages read. */
export interface FrontdoorV3 {
    [key: string]: any;

    id: string;
    name: string;
    /** The org this frontdoor belongs to. The list spans orgs, so selecting one switches the tenant to match. */
    organizationId?: string;
    /** The network the frontdoor is enabled on. Alerts are read per network, so the Alerts page scopes by it. */
    networkId?: string;
    /** The network group of that network. Grants on the network are checked under it. */
    networkGroupId?: string;
    status?: string;
    capabilities?: string[];
}

/** The fields a PATCH of a frontdoor may change. An omitted field stays as it is. */
export interface UpdateFrontdoorV3 {
    name?: string;
}

/** Identity-preference key holding the frontdoor the user chose to default to. */
export const DEFAULT_FRONTDOOR_ID_KEY = 'default_frontdoor_id';

/** The picker entry shown when the org has no v3 frontdoor at all. */
export const NO_FRONTDOOR_PLACEHOLDER: FrontdoorV3 = {
    id: '',
    name: 'No Frontdoors',
    isPlaceholder: true,
};

/** The picker entry shown when the org has several and the user has not chosen one yet. */
export const SELECT_FRONTDOOR_PLACEHOLDER: FrontdoorV3 = {
    id: '',
    name: 'Select A Frontdoor',
    isPlaceholder: true,
};

/**
 * Which frontdoor to select from `frontdoors`: the one from the last session, then the user's
 * saved default, then the only one on offer, otherwise a placeholder that prompts them to choose.
 * Both ids are checked against the list on purpose — they outlive a sign-out, so another user on
 * the same browser must not inherit one. Last session beats the saved default, matching how the
 * network picker resolves its own two sources.
 */
export function selectFrontdoor(frontdoors: FrontdoorV3[], storedId: string, preferredId = ''): FrontdoorV3 {
    if (!frontdoors?.length) {
        return NO_FRONTDOOR_PLACEHOLDER;
    }
    const known = (id: string) => (id ? frontdoors.find((frontdoor) => frontdoor.id === id) : undefined);
    const chosen = known(storedId) ?? known(preferredId);
    if (chosen) {
        return chosen;
    }
    if (frontdoors.length === 1) {
        return frontdoors[0];
    }
    return SELECT_FRONTDOOR_PLACEHOLDER;
}

/**
 * Reads the v3 frontdoor list. The server scopes it from the user token, so it spans every org
 * the user can reach and does not change when the console switches org. An empty list is the
 * normal answer for a user with no v3 access — it is the signal to use the v2 path, not an error.
 */
@Injectable({ providedIn: 'root' })
export class FrontdoorV3Service {
    private readonly apiUrl: string;

    // One list for the session. The server reads the scope from the token, so the answer does
    // not vary by org and a per-org cache would only re-ask for the same rows. Every v1/v2 user
    // pays this request once too and gets an empty list, so the empty answer is cached the same
    // way — an uncached miss would re-ask on every navigation for exactly the users who gain nothing.
    private listCache: Observable<FrontdoorV3[]> | null = null;

    constructor(
        private logger: LoggerService,
        @Inject(HTTP_CLIENT) private http: HttpClient,
        @Inject(ENVIRONMENT) private environment: Environment
    ) {
        this.apiUrl = this.environment.frontdoorURL;
    }

    /**
     * Every frontdoor the signed-in user can reach, across all their orgs. An empty list means
     * the user is not on v3 and the caller must fall back to the v2 org lookup.
     */
    public findFrontdoors(): Observable<FrontdoorV3[]> {
        if (this.listCache) {
            return this.listCache;
        }
        const request = this.http
            .get(`${this.apiUrl}/v3/`, { headers: { Accept: 'application/hal+json' } })
            .pipe(
                map((response: any) => this.readList(response)),
                catchError((error) => {
                    // A v1/v2 org answers with an empty list rather than an error, so a failure
                    // here is a real one. Report it as "not v3" so it degrades to the v2 path
                    // instead of blocking the app, and drop it from the cache so it retries.
                    this.logger.error('findFrontdoors failed', error);
                    this.listCache = null;
                    return of([] as FrontdoorV3[]);
                }),
                shareReplay({ bufferSize: 1, refCount: false })
            );
        this.listCache = request;
        return request;
    }

    /**
     * The frontdoors with the given ids that the caller may read. It bypasses the list cache, because a role can name a frontdoor
     * outside the signed-in user's own list. The server leaves out an id that the caller may not read.
     */
    public findFrontdoorsByIds(ids: string[]): Promise<FrontdoorV3[]> {
        const wantedIds = (ids || []).filter((id) => !!id);
        if (!wantedIds.length) {
            return Promise.resolve([]);
        }
        let params = new HttpParams().set('page', '0').set('size', `${wantedIds.length}`);
        wantedIds.forEach((id) => (params = params.append('id', id)));
        return firstValueFrom(
            this.http.get(`${this.apiUrl}/v3/`, { headers: { Accept: 'application/hal+json' }, params }).pipe(
                map((response: any) => this.readList(response)),
                catchError((error) => {
                    this.logger.error('findFrontdoorsByIds failed', error);
                    return of([] as FrontdoorV3[]);
                })
            )
        );
    }

    /** True when the user can reach at least one v3 frontdoor. */
    public isV3(): Observable<boolean> {
        return this.findFrontdoors().pipe(map((frontdoors) => frontdoors.length > 0));
    }

    /** Change a frontdoor and answer with the stored result. Clears the list cache, so a later read sees the change. */
    public updateFrontdoor(id: string, update: UpdateFrontdoorV3): Observable<FrontdoorV3> {
        return this.http
            .patch<FrontdoorV3>(`${this.apiUrl}/v3/${id}`, update, { headers: { Accept: 'application/hal+json' } })
            .pipe(tap(() => this.clearCache()));
    }

    /** Delete a frontdoor. The service answers once the account is gone; what it owns follows asynchronously. */
    public deleteFrontdoor(id: string): Observable<void> {
        return this.http.delete<void>(`${this.apiUrl}/v3/${id}`).pipe(tap(() => this.clearCache()));
    }

    /** Dry-run of {@link updateFrontdoor}: the violations the service reports, such as a name taken in the network. */
    public validateUpdateFrontdoor(id: string, update: UpdateFrontdoorV3): Observable<ValidationError[]> {
        return checkFrontdoorBody(this.http, `${this.apiUrl}/v3/${id}`, update, 'PATCH');
    }

    public clearCache() {
        this.listCache = null;
    }

    /** Accepts the HAL collection, a bare array, or a Spring page, so the shape can settle later. */
    private readList(response: any): FrontdoorV3[] {
        if (Array.isArray(response)) {
            return response;
        }
        const embedded = response?._embedded;
        if (embedded) {
            const collection = embedded.frontdoorList ?? embedded.frontdoors ?? Object.values(embedded)[0];
            if (Array.isArray(collection)) {
                return collection;
            }
        }
        return Array.isArray(response?.content) ? response.content : [];
    }
}
